import { type MetaFunction, Link } from 'react-router'
import { pageMeta } from '#app/utils/page-meta.ts'

export const meta: MetaFunction = (args) =>
	pageMeta(args, {
		title: 'KI-agent',
		shareTitle: 'KI-agent på Huskonsertfestivalen',
		description:
			'La din eigen KI-agent hjelpe deg med å reservere billettar og svare på spørsmål om Huskonsertfestivalen.',
	})

export default function AgentRoute() {
	return (
		<div className="mx-auto mb-24 mt-16 max-w-3xl px-6">
			<h1 className="text-h1">KI-agent på Huskonsertfestivalen</h1>
			<p className="mt-4 text-lg">
				Vi tilbyr eit offisielt «MCP-grensesnitt» — ein trygg måte for din eigen
				KI-agent (til dømes Claude, ChatGPT eller opencode) å snakke med
				festivalen på. Agenten kan då hjelpe deg med å reservere billettar,
				sjekke billettane dine og svare på spørsmål på vanleg språk.
			</p>

			<h2 className="text-h3 mt-10">Kva kan agenten gjere?</h2>
			<ul className="mt-4 list-disc space-y-2 pl-6">
				<li>
					Sjå programmet: kva konsertar som skjer, kvar og når, og kor mange
					plassar som er igjen.
				</li>
				<li>
					Reservere billettar for deg og familien (blir betalt separat, som
					vanleg).
				</li>
				<li>Seie frå kva billettar du har, og avbestille for deg.</li>
				<li>Svare på spørsmål om festivalen med enkle ord.</li>
			</ul>
			<p className="text-muted-foreground mt-4">
				Agenten kan berre gjere det du sjølv kan gjere når du er logga inn —
				ikkje meir, ikkje mindre.
			</p>

			<h2 className="text-h3 mt-10">Slik set du det opp</h2>
			<p>
				Du treng ikkje installere noko — agenten din pratar rett med festivalen
				over nettet. Det einaste du treng, er ein API-nøkkel.
			</p>
			<ol className="mt-4 list-decimal space-y-6 pl-6">
				<li>
					<strong>Lag ein API-nøkkel.</strong>{' '}
					<Link
						to="/settings/profile/api-tokens"
						className="hover:text-foreground underline"
					>
						Gå til innstillingane
					</Link>{' '}
					medan du er logga inn, og trykk «Lag nøkkel». Nøkkelen blir berre vist
					éin gong — kopier han og lagre han på ein trygg stad.
				</li>
				<li>
					<strong>Kople agenten din til festivalen.</strong> Nedanfor følgjer
					ferdig oppsett for dei vanlegaste agentane — lim inn, byt ut{' '}
					<code className="bg-muted rounded px-1 font-mono text-sm">
						mcp_din-nøkkel
					</code>{' '}
					med nøkkelen din, og du er i gang.
					<p className="mb-1 mt-4 text-sm font-semibold">opencode</p>
					<p className="text-muted-foreground mb-2 text-sm">
						Legg inn i <code>opencode.json</code> (eller lag ei ny med{' '}
						<code>opencode auth login</code>-flyten):
					</p>
					<pre className="bg-muted overflow-x-auto rounded-md p-4 font-mono text-sm">{`{
  "mcp": {
    "huskonsertfestivalen": {
      "type": "remote",
      "url": "https://huskonsertfestivalen.fly.dev/resources/mcp/http",
      "headers": {
        "Authorization": "Bearer mcp_din-nøkkel"
      },
      "enabled": true
    }
  }
}`}</pre>
					<p className="mb-1 mt-4 text-sm font-semibold">
						Claude Code / Claude Desktop
					</p>
					<pre className="bg-muted overflow-x-auto rounded-md p-4 font-mono text-sm">{`# Claude Code (i terminalen):
claude mcp add --transport http huskonsertfestivalen \\
  https://huskonsertfestivalen.fly.dev/resources/mcp/http \\
  --header "Authorization: Bearer mcp_din-nøkkel"

# Claude Desktop (claude_desktop_config.json):
{
  "mcpServers": {
    "huskonsertfestivalen": {
      "type": "http",
      "url": "https://huskonsertfestivalen.fly.dev/resources/mcp/http",
      "headers": {
        "Authorization": "Bearer mcp_din-nøkkel"
      }
    }
  }
}`}</pre>
					<p className="text-muted-foreground mt-2 text-sm">
						Andre verktøy (Cursor, ChatGPT osb.) følgjer same oppskrift: lim inn
						URL-en{' '}
						<code>https://huskonsertfestivalen.fly.dev/resources/mcp/http</code>{' '}
						og Authorization-headeren med nøkkelen din i MCP-innstillingane.
					</p>
				</li>
				<li>
					<strong>Prøv det!</strong> Spør agenten til dømes: «Kva konsertar er
					det på festivalen i år?» eller «Reserver to vaksenbillettar til
					laurdagskonserten for Ola og Per».
				</li>
			</ol>

			<h2 className="text-h3 mt-10">Er det trygt?</h2>
			<p className="mt-4">
				Nøkkelen fungerer som ei innlogging: alt agenten gjer, skjer som deg,
				med rettane dine, og alt blir loggført som vanleg. Agenten kan til dømes
				ikkje sjå billettane til andre eller endre festivalen. Du kan slette
				nøkkelen når som helst i{' '}
				<Link
					to="/settings/profile/api-tokens"
					className="hover:text-foreground underline"
				>
					innstillingane
				</Link>
				, og då mistar agenten tilgangen med ein gong. Nøkkelen skal aldri
				delast med andre — han er som eit passord.
			</p>
		</div>
	)
}
